import { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MyContext } from '../../context';
import DataTable from '../../components/tables/DataTable';
import Pagination from '../../components/Pagination';
import StatCard from '../../components/StatCard';
import StatusBadge from '../../components/StatusBadge';
import ExportButton from '../../components/ExportButton';
import DateRangePicker from '../../components/DateRangePicker';
import { listOrders } from '../../utils/queries';
import { getListStatus } from '../../utils/orderStatus';

const PAGE_SIZE = 20;

export default function OrdersReport() {
	const { activeStoreId } = useContext(MyContext);
	const [range, setRange] = useState({});
	const [orders, setOrders] = useState([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(0);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		setLoading(true);
		listOrders({
			limit: PAGE_SIZE,
			offset: page * PAGE_SIZE,
			storeId: activeStoreId || undefined,
			dateFrom: range.from || undefined,
			dateTo: range.to || undefined,
		}).then((result) => {
			if (result?.success) {
				setOrders(result.data.orders);
				setTotal(result.data.pagination.total);
			}
			setLoading(false);
		});
	}, [activeStoreId, range.from, range.to, page]);

	const columns = [
		{
			key: 'order_number',
			header: 'Order Number',
			render: (value, row) => {
				const isTestModeOrder = (row.mode || 'test') === 'test';
				return (
					<Link to={`/orders/${row.order_id}`} className='text-black font-medium hover:underline'>
						{value}
						{isTestModeOrder && (
							<span className='ml-2 inline-block rounded-full bg-amber-50 text-amber-700 px-2 py-0.5 text-xs font-medium'>
								TEST
							</span>
						)}
					</Link>
				);
			},
		},
		{
			key: 'order_date',
			header: 'Order Date',
			render: (value) => (value ? new Date(value).toLocaleString() : ''),
		},
		{
			key: 'customer_name',
			header: 'Customer',
			render: (value) => value || '-',
		},
		{
			key: 'total_price',
			header: 'Total',
			render: (value, row) =>
				row.payment_type === 'LOYALTY_POINTS'
					? `${Math.round(Number(row.points_required)).toLocaleString()} pts`
					: value,
		},
		{
			key: 'state',
			header: 'Status',
			render: (_value, row) => {
				const { text, bucket } = getListStatus({
					state: row.state,
					erpOrderNumber: row.erp_order_number,
					paceJobStatus: row.pace_job_status,
					customFulfillmentOnly: row.custom_fulfillment_only,
					shipped: row.shipped,
				});
				return <StatusBadge status={bucket} text={text} />;
			},
		},
	];

	return (
		<div className='py-8 pr-8'>
			<div className='flex items-center justify-between mb-6'>
				<h1 className='text-2xl font-light tracking-tight'>Orders Report</h1>
				<ExportButton
					fileName='orders-report'
					sections={[
						{
							data: orders,
							columns: columns.map((c) => c.header),
							columnKeys: columns.map((c) => c.key),
						},
					]}
				/>
			</div>

			<div className='mb-6'>
				<DateRangePicker
					value={range}
					onChange={(item) => {
						setRange(item);
						setPage(0);
					}}
					className='w-72'
				/>
			</div>

			<div className='grid grid-cols-4 gap-4 mb-8'>
				<StatCard label='Total Orders' value={total} />
			</div>

			<DataTable columns={columns} data={orders} loading={loading} emptyHeading='No orders in range' />
			<Pagination pageCount={Math.ceil(total / PAGE_SIZE)} currentPage={page} onPageChange={setPage} />
		</div>
	);
}
